import React, {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { ProcessState, ProcessBase } from "types/process";
import {
  ModalDialogConfirmStatus,
  ModalDialogConfirmStatusProps,
} from "./ModalDialogConfirmStatus";
import {
  useProcessPolling,
  UseProcessPollingOptions,
} from "hooks/useProcessPolling";

export interface ModalDialogConfirmStatusWithPollingProps<
  TStartResponse,
  TProcess extends ProcessBase
> extends Omit<
    ModalDialogConfirmStatusProps,
    | "processState"
    | "percentComplete"
    | "statusMessage"
    | "onOKButtonClick"
    | "resultSection"
  > {
  /**
   * Polling configuration (start + getProcess)
   */
  processPollingOptions: UseProcessPollingOptions<TStartResponse, TProcess>;

  /**
   * Optional: build a result section once the process completes
   */
  buildResultSection?: (process: TProcess) => ReactNode;

  /**
   * Reset polling state when dialog opens
   * Default: true
   */
  resetOnOpen?: boolean;
}

export function ModalDialogConfirmStatusWithPolling<
  TStartResponse,
  TProcess extends ProcessBase
>(props: ModalDialogConfirmStatusWithPollingProps<TStartResponse, TProcess>) {
  const {
    //ModalDialogConfirmStatusWithPolling specific props
    processPollingOptions,
    buildResultSection,
    resetOnOpen = true,

    open,
    onClose,
    onCancelButtonClick,

    ...dialogProps
  } = props;

  //Get results from process polling
  const {
    process,
    processState,
    message,
    percentComplete,
    isBusy,
    isTerminal,
    error,
    start,
    reset,
  } = useProcessPolling<TStartResponse, TProcess>(processPollingOptions);

  // Reset on open (fresh dialog every time)
  const prevOpenRef = useRef<boolean>(open);
  useEffect(() => {
    const wasOpen = prevOpenRef.current;
    prevOpenRef.current = open;

    if (open && resetOnOpen && !wasOpen) {
      reset();
    }
  }, [open, reset, resetOnOpen]);

  //Handle setting our state and message to error messages if there was an error
  const derivedProcessState = error ? ProcessState.ERROR : processState;
  const derivedMessage = error
    ? error instanceof Error
      ? error.message
      : String(error)
    : message;

  //Callback to build a result section from the process object
  //for the dialog when the process terminates
  const resultSection = useMemo(() => {
    if (!process || !isTerminal) return undefined;
    return buildResultSection?.(process);
  }, [buildResultSection, isTerminal, process]);

  //Start the request when user clicks OK button
  const handleOk = useCallback(async () => {
    await start();
  }, [start]);

  //On clicking the close button, reset the dialog and run close callback
  const handleClose = useCallback(() => {
    if (isBusy) return; // <-- add this
    reset();
    onClose?.();
  }, [isBusy, onClose, reset]);

  //On clicking the Cancel button, reset the dialog and run cancel callback
  const handleCancel = useCallback(() => {
    if (isBusy) return; // <-- add this
    reset();
    onCancelButtonClick?.();
  }, [isBusy, onCancelButtonClick, reset]);

  //Paint the dialog,
  return (
    <ModalDialogConfirmStatus
      {...dialogProps}
      open={open}
      processState={derivedProcessState}
      percentComplete={percentComplete}
      statusMessage={derivedMessage}
      onOKButtonClick={handleOk}
      onClose={handleClose}
      onCancelButtonClick={handleCancel}
      resultSection={resultSection}
    />
  );
}
